// Shared ORDER PICKER for the sell-side doors (Add Discount, Add Fees, Make Payment): the existing Combobox, plus
// the owner's done-order rule — done orders are never offered; a typed done order id shows a disabled row linking to
// its order page; an empty state + a persistent footer point to Sales Orders filtered to Done. Rules: salesDoorContext.mjs.
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Combobox from '../Combobox';
import { typedOpId, typedOrderRow, DONE_ORDERS_HREF } from './salesDoorContext.mjs';

import { emoText } from '../../shell/EmojiIcons';
const NOTE = { fontSize: 11.5, color: '#64748b', marginTop: 6 };
const LINK = { background: 'var(--surface, #fff)', border: '1px solid var(--ctl-border)', borderRadius: 8, padding: '2px 10px', color: '#2d6a4f', fontWeight: 600, fontSize: 'inherit', cursor: 'pointer', fontFamily: 'inherit' };
const ROW = { marginTop: 6, padding: '6px 10px', border: '1px dashed #d6ddd6', borderRadius: 6, fontSize: 12.5, color: '#64748b', background: '#f8fafc' };

// A locked context value (order or customer), the Add Fees "· locked" chip.
export function LockedChip({ label, text, title }) {   // label defaults to 'Customer'
  const { t } = useTranslation('sales');
  if (!label) label = t('orderPicker.customer');
  return (
    <div style={{ marginBottom: 12 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#7a8a7f', marginBottom: 3, display: 'block', textTransform: 'uppercase', letterSpacing: '.05em' }}>{emoText(label)} · {t('orderPicker.locked')}</span>
      <div style={{ border: '1px solid var(--line)', borderRadius: 4, padding: '6px 10px', fontSize: 13, background: '#eef4f0', fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={title || text}>{text}</div>
    </div>
  );
}

// props: Combobox's (options, value, onChange, placeholder, style) + loaded (show the empty state only once loaded)
// + onLeave (close the drawer before navigating away).
export default function OpenOrderPicker({ options, value, onChange, placeholder, style, loaded = true, onLeave }) {
  const { t } = useTranslation('sales');
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [lookup, setLookup] = useState({});   // op -> undefined (in flight) | { notFound } | order body
  const op = typedOpId(query);
  const offeredIds = options.map(o => o.value);
  const known = op ? Object.prototype.hasOwnProperty.call(lookup, op) : true;

  // ONE lookup per typed id that is not offered (existing single-order endpoint), debounced.
  useEffect(() => {
    if (!op || known || offeredIds.some(id => String(id).toUpperCase() === op)) return undefined;
    const h = setTimeout(() => {
      setLookup(m => ({ ...m, [op]: undefined }));
      api.getSalesOrder(op)
        .then(d => setLookup(m => ({ ...m, [op]: d && d.header ? d : { notFound: true } })))
        .catch(() => setLookup(m => ({ ...m, [op]: { notFound: true } })));   // 404 (or unreadable) -> not offered
    }, 400);
    return () => clearTimeout(h);
  }, [op, known]); // eslint-disable-line react-hooks/exhaustive-deps

  const row = typedOrderRow({ query, offeredIds, lookup: lookup[op] });
  const go = (href) => { if (onLeave) onLeave(); navigate(href); };
  const doneLink = <button type="button" style={LINK} onClick={() => go(DONE_ORDERS_HREF)}>{t('orderPicker.doneLink')}</button>;

  return (
    <>
      {/* The typed-id row sits ABOVE the input: below it, the open "No matches" list would cover it. */}
      {row && (
        <div style={{ ...ROW, marginTop: 0, marginBottom: 6 }} aria-live="polite">
          {row.kind === 'checking' && t('orderPicker.checking', { op: row.op })}
          {row.kind === 'notFound' && t('orderPicker.notFound', { op: row.op })}
          {(row.kind === 'done' || row.kind === 'elsewhere') && (
            <button type="button" style={LINK} onClick={() => go(row.href)}>
              {t(row.kind === 'done' ? 'orderPicker.doneRow' : 'orderPicker.elsewhereRow', { op: row.op })}
            </button>
          )}
        </div>
      )}
      <Combobox value={value} onChange={v => { setQuery(''); onChange(v); }} onQueryChange={setQuery}
        placeholder={placeholder} options={options} style={style || { display: 'block', width: '100%' }} />
      {loaded && options.length === 0 && (
        <div style={{ ...ROW, borderStyle: 'solid' }}><b>{t('orderPicker.empty')}</b> — {t('orderPicker.emptyHint')}</div>
      )}
      <div style={NOTE}>{t('orderPicker.doneFooter')} {doneLink}</div>
    </>
  );
}
